(() => {
  if (window.PremiumLookbookSlider) {
    window.PremiumLookbookSlider.initAll();
    return;
  }

  const instances = new WeakMap();

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;

    const slides = [...root.querySelectorAll('[data-slide]')];
    root.dataset.initialized = 'true';

    if (slides.length < 2) {
      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === 0);
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });
      return;
    }

    const prevButton = root.querySelector('[data-prev]');
    const nextButton = root.querySelector('[data-next]');
    const dots = [...root.querySelectorAll('[data-dot]')];
    const current = root.querySelector('[data-current]');
    const pauseButton = root.querySelector('[data-pause]');
    const progress = root.querySelector('[data-progress]');
    const arrowsWrap = root.querySelector('[data-arrows]');

    const autoplayEnabled = root.dataset.autoplay === 'true';
    const pauseOnHover = root.dataset.pauseHover === 'true';
    const loop = root.dataset.loop === 'true';

    const interval = Math.max(
      2000,
      Number(root.dataset.autoplaySpeed) || 6000
    );

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      )
    );

    let timer = null;
    let pausedByUser = false;
    let pausedTemporarily = false;
    let touchStartX = null;

    const setFocusable = (slide, enabled) => {
      slide
        .querySelectorAll(
          'a, button, input, select, textarea, [tabindex]'
        )
        .forEach((el) => {
          if (enabled) {
            if (el.dataset.plOriginalTabindex !== undefined) {
              const original = el.dataset.plOriginalTabindex;

              if (original === '') {
                el.removeAttribute('tabindex');
              } else {
                el.setAttribute('tabindex', original);
              }

              delete el.dataset.plOriginalTabindex;
            } else if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          } else {
            if (el.dataset.plOriginalTabindex === undefined) {
              el.dataset.plOriginalTabindex =
                el.getAttribute('tabindex') || '';
            }

            el.setAttribute('tabindex', '-1');
          }
        });
    };

    const stopAutoplay = () => {
      if (timer) {
        window.clearInterval(timer);
      }

      timer = null;

      if (progress) {
        progress.style.transition = 'none';
      }
    };

    const resetProgress = () => {
      if (!progress) return;

      progress.style.transition = 'none';
      progress.style.transform = 'scaleX(0)';

      void progress.offsetWidth;

      if (
        autoplayEnabled &&
        !pausedByUser &&
        !pausedTemporarily &&
        !reduceMotion
      ) {
        progress.style.transition = `transform ${interval}ms linear`;
        progress.style.transform = 'scaleX(1)';
      }
    };

    const syncArrowPosition = () => {
      if (!arrowsWrap || !prevButton || !nextButton) return;

      const activeSlide = slides[activeIndex];
      const media = activeSlide?.querySelector(
        '.premium-lookbook__media'
      );

      const slider = root.querySelector(
        '.premium-lookbook__slider'
      );

      if (!media || !slider) return;

      const mediaRect = media.getBoundingClientRect();
      const sliderRect = slider.getBoundingClientRect();

      const top =
        mediaRect.top -
        sliderRect.top +
        mediaRect.height / 2;

      const left = Math.max(
        10,
        mediaRect.left -
          sliderRect.left +
          12
      );

      const right = Math.max(
        10,
        sliderRect.right -
          mediaRect.right +
          12
      );

      root.style.setProperty(
        '--pl-arrow-top',
        `${top}px`
      );

      root.style.setProperty(
        '--pl-arrow-left',
        `${left}px`
      );

      root.style.setProperty(
        '--pl-arrow-right',
        `${right}px`
      );
    };

    const updateControls = () => {
      dots.forEach((dot, index) => {
        const isActive =
          index === activeIndex;

        dot.classList.toggle(
          'is-active',
          isActive
        );

        dot.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });

      if (current) {
        current.textContent =
          String(activeIndex + 1).padStart(
            2,
            '0'
          );
      }

      if (!loop) {
        if (prevButton) {
          prevButton.disabled =
            activeIndex === 0;
        }

        if (nextButton) {
          nextButton.disabled =
            activeIndex ===
            slides.length - 1;
        }
      }
    };

    const startAutoplay = () => {
      stopAutoplay();
      resetProgress();

      if (
        !autoplayEnabled ||
        pausedByUser ||
        pausedTemporarily ||
        reduceMotion
      ) {
        return;
      }

      timer = window.setInterval(() => {
        if (
          !loop &&
          activeIndex ===
            slides.length - 1
        ) {
          stopAutoplay();
          return;
        }

        showSlide(
          activeIndex + 1,
          {
            restart: false
          }
        );

        resetProgress();
      }, interval);
    };

    const showSlide = (
      index,
      { restart = true } = {}
    ) => {
      if (loop) {
        index =
          (index + slides.length) %
          slides.length;
      } else {
        index = Math.max(
          0,
          Math.min(
            slides.length - 1,
            index
          )
        );
      }

      slides.forEach(
        (slide, slideIndex) => {
          const isActive =
            slideIndex === index;

          slide.classList.toggle(
            'is-active',
            isActive
          );

          slide.setAttribute(
            'aria-hidden',
            isActive
              ? 'false'
              : 'true'
          );

          setFocusable(
            slide,
            isActive
          );
        }
      );

      activeIndex = index;

      updateControls();

      window.requestAnimationFrame(
        syncArrowPosition
      );

      if (restart) {
        startAutoplay();
      }
    };

    const setTemporaryPause = (
      value
    ) => {
      pausedTemporarily = value;
      startAutoplay();
    };

    const onPrev = () => {
      showSlide(activeIndex - 1);
    };

    const onNext = () => {
      showSlide(activeIndex + 1);
    };

    const onPause = () => {
      pausedByUser =
        !pausedByUser;

      pauseButton?.classList.toggle(
        'is-paused',
        pausedByUser
      );

      pauseButton?.setAttribute(
        'aria-label',
        pausedByUser
          ? 'Start automatisk slider'
          : 'Pause automatisk slider'
      );

      startAutoplay();
    };

    const onKeydown = (
      event
    ) => {
      if (
        event.key ===
        'ArrowLeft'
      ) {
        showSlide(
          activeIndex - 1
        );
      }

      if (
        event.key ===
        'ArrowRight'
      ) {
        showSlide(
          activeIndex + 1
        );
      }
    };

    const onTouchStart = (
      event
    ) => {
      touchStartX =
        event.changedTouches?.[0]
          ?.clientX ?? null;
    };

    const onTouchEnd = (
      event
    ) => {
      if (
        touchStartX === null
      ) {
        return;
      }

      const touchEndX =
        event.changedTouches?.[0]
          ?.clientX ??
        touchStartX;

      const delta =
        touchEndX -
        touchStartX;

      touchStartX = null;

      if (
        Math.abs(delta) <
        45
      ) {
        return;
      }

      if (delta > 0) {
        showSlide(
          activeIndex - 1
        );
      } else {
        showSlide(
          activeIndex + 1
        );
      }
    };

    const onMouseEnter = () => {
      setTemporaryPause(true);
    };

    const onMouseLeave = () => {
      setTemporaryPause(false);
    };

    const onFocusIn = () => {
      setTemporaryPause(true);
    };

    const onFocusOut = (
      event
    ) => {
      if (
        !root.contains(
          event.relatedTarget
        )
      ) {
        setTemporaryPause(false);
      }
    };

    const onBlockSelect = (
      event
    ) => {
      const selected =
        root.querySelector(
          `[data-block-id="${event.detail.blockId}"]`
        );

      if (!selected) return;

      const index =
        slides.indexOf(
          selected
        );

      if (index < 0) return;

      pausedTemporarily = true;

      showSlide(
        index,
        {
          restart: false
        }
      );

      stopAutoplay();
    };

    const onBlockDeselect =
      () => {
        pausedTemporarily =
          false;

        startAutoplay();
      };

    const onResize = () => {
      window.requestAnimationFrame(
        syncArrowPosition
      );
    };

    const onImageLoad = () => {
      window.requestAnimationFrame(
        syncArrowPosition
      );
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };

    prevButton?.addEventListener(
      'click',
      onPrev
    );

    nextButton?.addEventListener(
      'click',
      onNext
    );

    pauseButton?.addEventListener(
      'click',
      onPause
    );

    dots.forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          showSlide(
            Number(
              dot.dataset.dot
            )
          );
        }
      );
    });

    root.addEventListener(
      'keydown',
      onKeydown
    );

    root.addEventListener(
      'touchstart',
      onTouchStart,
      {
        passive: true
      }
    );

    root.addEventListener(
      'touchend',
      onTouchEnd,
      {
        passive: true
      }
    );

    if (pauseOnHover) {
      root.addEventListener(
        'mouseenter',
        onMouseEnter
      );

      root.addEventListener(
        'mouseleave',
        onMouseLeave
      );

      root.addEventListener(
        'focusin',
        onFocusIn
      );

      root.addEventListener(
        'focusout',
        onFocusOut
      );
    }

    document.addEventListener(
      'visibilitychange',
      onVisibility
    );

    window.addEventListener(
      'resize',
      onResize,
      {
        passive: true
      }
    );

    root
      .querySelectorAll(
        '.premium-lookbook__image'
      )
      .forEach((image) => {
        image.addEventListener(
          'load',
          onImageLoad
        );
      });

    document.addEventListener(
      'shopify:block:select',
      onBlockSelect
    );

    document.addEventListener(
      'shopify:block:deselect',
      onBlockDeselect
    );

    slides.forEach(
      (slide, index) => {
        setFocusable(
          slide,
          index === activeIndex
        );
      }
    );

    updateControls();

    window.requestAnimationFrame(
      syncArrowPosition
    );

    startAutoplay();

    instances.set(root, {
      destroy() {
        stopAutoplay();

        prevButton?.removeEventListener(
          'click',
          onPrev
        );

        nextButton?.removeEventListener(
          'click',
          onNext
        );

        pauseButton?.removeEventListener(
          'click',
          onPause
        );

        root.removeEventListener(
          'keydown',
          onKeydown
        );

        root.removeEventListener(
          'touchstart',
          onTouchStart
        );

        root.removeEventListener(
          'touchend',
          onTouchEnd
        );

        root.removeEventListener(
          'mouseenter',
          onMouseEnter
        );

        root.removeEventListener(
          'mouseleave',
          onMouseLeave
        );

        root.removeEventListener(
          'focusin',
          onFocusIn
        );

        root.removeEventListener(
          'focusout',
          onFocusOut
        );

        document.removeEventListener(
          'visibilitychange',
          onVisibility
        );

        window.removeEventListener(
          'resize',
          onResize
        );

        root
          .querySelectorAll(
            '.premium-lookbook__image'
          )
          .forEach(
            (image) => {
              image.removeEventListener(
                'load',
                onImageLoad
              );
            }
          );

        document.removeEventListener(
          'shopify:block:select',
          onBlockSelect
        );

        document.removeEventListener(
          'shopify:block:deselect',
          onBlockDeselect
        );

        root.dataset.initialized =
          'false';
      }
    });
  };

  const initAll = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        '[data-premium-lookbook]'
      )
    ) {
      init(scope);
    }

    scope
      .querySelectorAll?.(
        '[data-premium-lookbook]'
      )
      .forEach(init);
  };

  const destroy = (root) => {
    instances
      .get(root)
      ?.destroy();

    instances.delete(root);
  };

  window.PremiumLookbookSlider = {
    init,
    initAll,
    destroy
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.matches?.(
          '[data-premium-lookbook]'
        )
          ? event.target
          : event.target.querySelector?.(
              '[data-premium-lookbook]'
            );

      if (root) {
        destroy(root);
      }
    }
  );
})();